import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Tooltip, Grid } from '@mui/material';
import { cloudinaryFull } from '../../../../utils/formatCloudinary';
import { fCurrency } from '../../../../utils/formatNumber';
import AddToCartButton from './AddToCartButton';
import PreviouslyOrderedColumn from '../order/PreviouslyOrderedColumn';
import Iconify from '../../../../reusable-components/iconify';

ItemCard.propTypes = {
  row: PropTypes.object.isRequired,
  onOpenDetails: PropTypes.func.isRequired,
  editingItemId: PropTypes.number,
  setEditingItemId: PropTypes.func,
};

export default function ItemCard({ row, onOpenDetails, editingItemId, setEditingItemId }) {

  const details = row.productDetails[0] || {};
  const { pictures } = details;

  const useOverflowTooltip = (value) => {
    const ref = useRef(null);
    const [isOverflowed, setIsOverflowed] = useState(false);

    useEffect(() => {
      const el = ref.current;
      if (el) setIsOverflowed(el.scrollWidth > el.clientWidth);
    }, [value]);

    return { ref, isOverflowed };
  };
  const descOverflow = useOverflowTooltip(row.description);
  const manOverflow = useOverflowTooltip(details?.manufacturer);

  return (
    <Box
      sx={{
        borderRadius: 2,
        p: 3,
        height: '100%',
        width: '100%',
        backgroundColor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        border: '2px solid #ccc',
      }}
    >
      <Box
        onClick={onOpenDetails}
        sx={{
          height: 150,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
          cursor: 'pointer',
          backgroundColor: '#ffffff',
        }}
      >
        <Box
          component="img"
          alt="attachment"
          src={
            pictures?.length
              ? cloudinaryFull(pictures[0])
              : cloudinaryFull(
                  'https://res.cloudinary.com/agoraserv/image/upload/v1738258682/No%20Image%20Animation.gif'
                )
          }
          sx={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain', display: 'block' }}
        />
      </Box>

      <Box sx={{ width: '100%' }}>
        <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Grid item>
            <Typography variant="body1" fontWeight="bold" sx={{ textAlign: 'left' }}>
              {row?.invalidItem ? '' : fCurrency(details.price)}
            </Typography>
          </Grid>
          <Grid item>
            {details.previouslyOrderedItems?.length > 0 && (
              <Tooltip
                arrow
                placement="top"
                title={
                  <Box sx={{ p: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Item history:
                    </Typography>

                    <PreviouslyOrderedColumn
                      sx={{ fontSize: '0.875rem' }}
                      previouslyOrderedItems={details.previouslyOrderedItems || []}
                    />
                  </Box>
                }
              >
                <Iconify icon="material-symbols:history" sx={{ color: 'gray', cursor: 'pointer' }} />
              </Tooltip>
            )}
          </Grid>
        </Grid>

        <Tooltip title={descOverflow.isOverflowed ? row.description : ''} placement="top" arrow>
          <Typography
            ref={descOverflow.ref}
            variant="body2"
            sx={{
              textAlign: 'left',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {row?.description}
          </Typography>
        </Tooltip>
        <Typography variant="body2">
          PS{row.id}
        </Typography>
        <Tooltip title={manOverflow.isOverflowed ? details.manufacturer : ''} placement="bottom" arrow>
          <Typography
            ref={manOverflow.ref}
            variant="caption"
            color="textSecondary"
            sx={{
              textAlign: 'left',
              display: 'block',
              minHeight: '1em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
            }}
          >
            {details?.manufacturer || '\u00A0'}
          </Typography>
        </Tooltip>
      </Box>
      <Box sx={{ mt: 1 }}>
        {row.invalidItem ? null : (
          <AddToCartButton item={row} editingItemId={editingItemId} setEditingItemId={setEditingItemId} />
        )}
      </Box>
    </Box>
  );
}
